import _ from 'lodash';
import angular from 'angular';
import moment from 'moment';
import sanitize from 'sanitize-filename';
import { EMPLOYER_WORKFLOW_STATUS } from '../../employer/constants/employer-workflow';
import { RECRUITER_WORKFLOW_STATUS } from '../../recruiter/constants/recruiter-workflow';

const WORKFLOWS_STEPS = [
    {
        step: EMPLOYER_WORKFLOW_STATUS.REVIEWED,
        labelKey: 'CLAIMED_EMPLOYER',
    },
    {
        step: EMPLOYER_WORKFLOW_STATUS.INTERVIEW_AND_ASSESSMENT,
        labelKey: 'INTERVIEW_AND_ASSESSMENT_EMPLOYER',
    },
    {
        step: EMPLOYER_WORKFLOW_STATUS.HIRED,
        labelKey: 'HIRED_EMPLOYER',
    },
    {
        step: EMPLOYER_WORKFLOW_STATUS.REJECTED,
        labelKey: 'REJECTED_EMPLOYER',
    },
];

const FEEDBACK_STATUSES = {
    NOT_RATED: 'Not rated',
    THUMBS_UP: 'Thumbs Up',
    THUMBS_DOWN: 'Thumbs Down',
    EMPTY: '',
};

const SEARCH_PAGE_ADDITIONAL_COLUMNS = [{
    count: 1,
    type: 'value',
    columnName: 'EXPORT_LIST_MATCH_SCORE',
    key: {
        parentKey: 'matchScore',
    },
}, {
    count: 1,
    type: 'value',
    columnName: 'EXPORT_LIST_TALENT_ID',
    key: {
        parentKey: 'talentId',
    },
}, {
    count: 1,
    type: 'value',
    columnName: 'EXPORT_LIST_CREATE_DATE',
    key: {
        parentKey: 'creationDate',
    },
}, {
    count: 1,
    type: 'value',
    columnName: 'EXPORT_LIST_UPDATED_DATE',
    key: {
        parentKey: 'updateDate',
    },
}, {
    count: 1,
    type: 'value',
    columnName: 'EXPORT_LIST_MAIN_RESUME_SOURCE',
    key: {
        parentKey: 'mainResumeSource',
    },
}, {
    count: 1,
    type: 'value',
    columnName: 'EXPORT_LIST_LAST_REFRESHED_DATE',
    key: {
        parentKey: 'lastRefreshedDate',
    },
}];

const SKIP_COLUMNS_FOR_SEARCH_PAGE = ['PUBLIC_APPROVAL_URL'];

const NOT_APPLICABLE = 'n/a';

export const CandidatesListService = (
    $filter,
    buildCSVService,
    CandidateFitTooltipService,
    CandidateService,
    CandidatesExportService,
    CommonCodeService,
    EmployerFeedbackService,
    RecruiterFeedbackService,
    EmployerService,
    EmployerRepService,
    PositionService,
    PublicProfileLinkGenerator,
    RecruiterCandidateService,
    RouteService,
    TalentForEmployerService,
    TalentScoreService,
    TalentService,
    WorkflowService,
    utils,
) => {
    const getCsvColumns = () => RouteService.getSystemPreference('csvCandidatesPrint');

    const getSearchCSVColumns = () => getCsvColumns()
        .concat(SEARCH_PAGE_ADDITIONAL_COLUMNS)
        .filter(column => !SKIP_COLUMNS_FOR_SEARCH_PAGE.includes(column.columnName));

    const getCsvHeaders = csvColumns => (csvColumns || []).map(({ columnName }) => $filter('translate')(columnName));

    const getCvsMetadata = (isSearchPage) => {
        const columns = isSearchPage ? getSearchCSVColumns() : getCsvColumns();
        const headers = getCsvHeaders(columns);
        return {
            columns,
            headers,
        };
    };

    const replaceWithProfileForSubmissionData = ({ candidate, talentsForEmployer }) => {
        const candidateCopy = angular.copy(candidate);
        const talentForEmployerCandidateData = _.find(
            talentsForEmployer,
            talentForEmployer => candidate.talentID === talentForEmployer.talentId,
        );
        if (talentForEmployerCandidateData) {
            candidateCopy.candidateData = talentForEmployerCandidateData;
        }
        return candidateCopy;
    };

    const getCandidateFirstAndLastName = (candidate) => {
        const [firstName = '', ...rest] = (candidate.name || '').trim().split(' ');
        return {
            firstName: firstName || '****',
            lastName: rest.pop() || '****',
        };
    };

    const isPublicEmailDomain = (email = '', publicEmailDomains) => {
        const emailDomain = utils.getEmailDomain(email) || '';
        const emailDomainWithPrefix = `@${emailDomain.toLowerCase()}`;

        return (publicEmailDomains || []).some(publicDomain => (
            emailDomainWithPrefix.indexOf(`.${publicDomain.toLowerCase()}.`) !== -1 ||
            emailDomainWithPrefix.indexOf(`@${publicDomain.toLowerCase()}.`) !== -1
        ));
    };

    const getSortedEmails = ({ emails = [], publicEmailDomains = [] }) => {
        if (Array.isArray(emails)) {
            return [...emails].sort((a, b) => {
                const isPublicDomainA = isPublicEmailDomain(a, publicEmailDomains);
                const isPublicDomainB = isPublicEmailDomain(b, publicEmailDomains);

                if (isPublicDomainA && !isPublicDomainB) {
                    return -1;
                }

                if (!isPublicDomainA && isPublicDomainB) {
                    return 1;
                }

                return 0;
            });
        }
        return [];
    };

    const getRatingStatus = (candidate) => {
        if (CandidateFitTooltipService.getGoodFitVisibilityState(candidate)) {
            return FEEDBACK_STATUSES.THUMBS_UP;
        }
        if (CandidateFitTooltipService.getBadFitVisibilityState(candidate)) {
            return FEEDBACK_STATUSES.THUMBS_DOWN;
        }
        if (CandidateFitTooltipService.getPendingVisibilityState(candidate)) {
            return FEEDBACK_STATUSES.NOT_RATED;
        }
        return FEEDBACK_STATUSES.EMPTY;
    };

    const getCandidateLocation = (candidate) => {
        const { location, residency } = candidate;

        if (location) {
            const { city, state, region, country } = location;
            return utils.joinTokens([city, state, region, country], ', ');
        }

        if (residency) {
            const { city, state, region, country } = residency;
            return utils.joinTokens([city, state, region, country], ', ');
        }

        return '';
    };

    const getCandidateCurrentJob = (candidate) => {
        const currentJobTitles = _.get(candidate, 'employmentHistory.0.titles', []) || [];
        const titles = currentJobTitles.map(({ title }) => title);

        return utils.joinTokens(titles, ', ');
    };

    const getLastEmployerActionAndDate = ({ candidateWithWorkflowData }) => {
        const employerActionRaw = _.get(candidateWithWorkflowData, 'workflow.employerStatus.current', NOT_APPLICABLE);
        const isValidEmployerAction = !['None', NOT_APPLICABLE].includes(employerActionRaw);

        if (isValidEmployerAction) {
            const step = WORKFLOWS_STEPS.find(item => item.step === employerActionRaw);
            const employerActionTranslationId = _.get(step, 'labelKey', NOT_APPLICABLE);
            const employerAction = $filter('translate')(employerActionTranslationId);

            const employerLastActionDate = _.get(candidateWithWorkflowData, 'workflow.employerStatus.timestamp');
            if (employerLastActionDate) {
                const employerActionDate = moment.utc(employerLastActionDate).format('MMM/DD/YYYY');
                return {
                    employerAction,
                    employerActionDate,
                };
            }
        }

        return {};
    };

    const getWorkflowNameToDisplay = (workflowName) => {
        let workflowNameToDisplay = workflowName;
        if (workflowName === 'Feedback') {
            workflowNameToDisplay = 'EMPLOYER_FEEDBACK_TAB_LABEL';
        }
        return $filter('translate')(workflowNameToDisplay);
    };

    const getFormattedDate = () => moment.utc().format('MM_DD_YY');

    const getCsvFileName = ({ workflowName = '', jobTitle = '', employerName = '' }) => {
        const SEPARATOR = ' - ';
        const fileNameParts = [];
        const formattedDate = getFormattedDate();
        const talenyaPrefix = 'TALENYA';
        if (workflowName) {
            const workflowNameToDisplay = getWorkflowNameToDisplay(workflowName);
            fileNameParts.push(talenyaPrefix, workflowNameToDisplay, jobTitle, employerName, formattedDate);
        } else {
            fileNameParts.push(talenyaPrefix, jobTitle, employerName, formattedDate);
        }

        const fileName = fileNameParts.filter(Boolean).join(SEPARATOR);
        const fileExtension = 'csv';
        return sanitize(`${fileName}.${fileExtension}`);
    };

    const getCandidateForCsv = ({ candidate = {}, publicEmailDomains }) => {
        const candidateWithWorkflowData = candidate;
        const candidateData = _.get(candidate, 'candidateData');
        const candidateFeedback = CandidateFitTooltipService.getFeedbackFromCandidate(candidateWithWorkflowData);

        const { firstName, lastName } = getCandidateFirstAndLastName(candidateData);
        const talentLocation = getCandidateLocation(candidateData);
        const currentEmployer = _.get(candidateData, 'employmentHistory.0.employerOrgName', '');
        const currentJob = getCandidateCurrentJob(candidateData);

        const emails = _.get(candidateData, 'contact.emailAddress', []);
        const sortedEmails = getSortedEmails({ emails, publicEmailDomains });
        const [email1, email2] = sortedEmails;

        const mobilePhones = _.get(candidateData, 'contact.mobile', []);
        const telephones = _.get(candidateData, 'contact.telephone', []);
        const allPhones = [...(mobilePhones || []), ...(telephones || [])];
        const phone1 = allPhones.shift();
        const phone2 = allPhones.shift();

        const { employerAction, employerActionDate } = getLastEmployerActionAndDate({ candidateWithWorkflowData });

        const ratingStatus = getRatingStatus(candidateWithWorkflowData);
        const ratingBy = _.get(candidateFeedback, 'feedbackProviderName');
        const ratingDate = CandidateFitTooltipService.getFeedbackDate(candidateWithWorkflowData);
        const isCandidateHasWorkflow = _.has(candidateWithWorkflowData, 'workflow');

        let publicProfileUrl = '';
        if (isCandidateHasWorkflow) {
            publicProfileUrl = PublicProfileLinkGenerator.generateLink(candidateWithWorkflowData, { isNamedProfile: true });
        }

        const webAddresses = _.get(candidateData, 'contact.webAddress', []);
        const linkedinWebAddress = _.find(webAddresses, { type: 'linkedin' });
        const linkedinUrl = _.get(linkedinWebAddress, 'url');

        const matchScore = _.get(candidateData, 'matchScore');
        const talentId = _.get(candidate, 'talentId');
        const creationDate = _.get(candidate, 'creationDate');
        const lastRefreshedDate = _.get(candidate, 'lastRefreshedDate');
        const updateDate = _.get(candidate, 'updateDate');
        const mainResumeSource = _.get(candidateData, 'mainResumeSource');
        const publicApprovalUrl = _.get(candidate, 'publicApprovalUrl');

        return {
            firstName,
            lastName,
            talentLocation,
            currentEmployer,
            currentJob,
            email1,
            email2,
            phone1,
            phone2,
            employerAction,
            employerActionDate,
            ratingStatus,
            ratingBy,
            ratingDate,
            talenyaUrl: publicProfileUrl,
            linkedinUrl,
            matchScore,
            talentId,
            creationDate,
            lastRefreshedDate,
            updateDate,
            mainResumeSource,
            publicApprovalUrl,
        };
    };

    const getCandidatesCsvData = async ({ candidates, columns, publicEmailDomains, positionId, isEmployerMode, alphabeticalOrderByName }) => {
        let talentsForEmployer = [];

        if (!isEmployerMode) {
            ({ result: talentsForEmployer } = await TalentForEmployerService.searchTalentForEmployer({
                positionId,
                anonymizeTalents: false,
            }));
        }

        const formattedCandidates = (candidates || [])
            .map(candidate => (isEmployerMode ? candidate : replaceWithProfileForSubmissionData({
                candidate,
                talentsForEmployer,
            })))
            .map(candidate => getCandidateForCsv({ candidate, publicEmailDomains }));

        if (alphabeticalOrderByName) {
            formattedCandidates.sort((a, b) => {
                const afirstNameLowered = a.firstName.toLowerCase();
                const bfirstNameLowered = b.firstName.toLowerCase();
                const alastNameLowered = a.lastName.toLowerCase();
                const blastNameLowered = b.lastName.toLowerCase();
                if (afirstNameLowered < bfirstNameLowered) {
                    return -1;
                }
                if (afirstNameLowered > bfirstNameLowered) {
                    return 1;
                }
                if (alastNameLowered < blastNameLowered) {
                    return -1;
                }
                if (alastNameLowered > blastNameLowered) {
                    return 1;
                }
                return 0;
            });
        }

        return buildCSVService.getCsvFile(formattedCandidates, columns);
    };

    const filterValidCandidatesForExport = ({ candidates = [], isEmployerMode, isExportAll }) => candidates.filter((candidate) => {
        const currentEmployerStatus = _.get(candidate, 'workflow.employerStatus.current');
        const isAutoFeedbackCandidate = _.get(candidate, 'workflow.feedback.type') === 'automatic';
        const isFeedbackRequested = Boolean(_.get(candidate, 'workflow.feedback')) && !isAutoFeedbackCandidate;
        const isContactCandidate = currentEmployerStatus === EMPLOYER_WORKFLOW_STATUS.NONE && _.get(candidate, 'workflow.feedback.grades.[0].grade') === 1;
        const isClaimedByEmployerCandidate = [
            EMPLOYER_WORKFLOW_STATUS.REVIEWED,
            EMPLOYER_WORKFLOW_STATUS.INTERVIEW_AND_ASSESSMENT,
            EMPLOYER_WORKFLOW_STATUS.HIRED,
            EMPLOYER_WORKFLOW_STATUS.REJECTED,
        ].includes(currentEmployerStatus);

        if (isEmployerMode) {
            if (isExportAll) {
                return !isAutoFeedbackCandidate && (isFeedbackRequested || isClaimedByEmployerCandidate || isContactCandidate);
            }
            return isAutoFeedbackCandidate || isFeedbackRequested || isContactCandidate || isClaimedByEmployerCandidate;
        }
        return true;
    });

    const getCandidatesWithHiddenDetails = (
        {
            candidates, feedbackCandidates, params = { candidateDataParamsToKeep: [] },
        }) => {

        const feedbackCandidatesWithTalentIdKeys = _.keyBy(feedbackCandidates, 'talentId');
        return candidates.map((candidate) => {
            const feedbackCandidate = feedbackCandidatesWithTalentIdKeys[candidate.talentId];
            if (feedbackCandidate) {
                const partialOriginalCandidate = {};

                if (!_.isEmpty(params.candidateDataParamsToKeep)) {
                    _.forEach(params.candidateDataParamsToKeep, (param) => {
                        if (candidate.candidateData[param]) {
                            partialOriginalCandidate[param] = candidate.candidateData[param];
                        }
                    });
                }

                candidate.candidateData = { ...feedbackCandidate, ...partialOriginalCandidate };
                candidate.fullName = feedbackCandidate.name;
            }
            return candidate;
        });
    };

    const getFeedbackCandidatesByUserType = async ({ isEmployerMode, positionId, employerId, recruiterId }) => {
        const pageOptions = {
            page: 0,
            size: 50,
            skip: 0,
        };

        const { list: feedbackCandidatesFirstPage = [], total: feedbackCandidatesTotal } = await (
            isEmployerMode ? EmployerFeedbackService.getFeedbackCandidates({
                positionId,
                employerId,
                isExplainAllTalents: false,
                explainTalentIds: [],
                isListView: true,
                pageOptions,
            }) : RecruiterFeedbackService.getFeedbackCandidates({
                positionId,
                recruiterId,
                isExplainAllTalents: false,
                explainTalentIds: [],
                isListView: true,
                pageOptions,
            })
        );

        let result = [...feedbackCandidatesFirstPage];

        const promises = [];
        let candidatesToFetch = feedbackCandidatesTotal - feedbackCandidatesFirstPage.length;
        while (candidatesToFetch > 0) {
            candidatesToFetch -= pageOptions.size;
            const pageOptionsNext = { ...pageOptions, page: ++pageOptions.page };
            const promise = isEmployerMode ? EmployerFeedbackService.getFeedbackCandidates({
                positionId,
                employerId,
                isListView: true,
                pageOptions: pageOptionsNext,
            }) : RecruiterFeedbackService.getFeedbackCandidates({
                positionId,
                recruiterId,
                isListView: true,
                pageOptions: pageOptionsNext,
            });
            promises.push(promise);
        }

        const feedbackCandidatesResults = await Promise.all(promises);

        for (const onChunk of feedbackCandidatesResults) {
            const { list } = onChunk;
            result = [...result, ...list];
        }

        return result;
    };

    const addExplainsDataToCandidates = async ({ candidates, positionData }) => {
        const chunkedTalentIds = _.chunk(_.map(candidates, 'talentId'), 50);

        const promises = [];

        for (const oneChunk of chunkedTalentIds) {
            promises.push(TalentScoreService.postTalentExplainScore({
                talentIDs: oneChunk,
                filter: positionData.desiredCandidateProfile,
            }));
        }

        const explainScoreResponses = await Promise.all(promises);
        let candidatesWithExplains = [...candidates];

        for (const { result: talentExplains = [] } of explainScoreResponses) {
            candidatesWithExplains = CandidateService.addExplainsDataToCandidates({
                candidates: candidatesWithExplains,
                talentExplains,
            });
        }
        return candidatesWithExplains;
    };

    const addFeedbackDataToCandidates = ({ candidates, positionData }) => {
        const candidatesWithFeedback = [...candidates];
        for (const oneFeedbackOnCandidate of positionData.feedbackOnCandidates) {
            const candidate = _.find(candidatesWithFeedback, { talentId: oneFeedbackOnCandidate.talentId });
            if (candidate) {
                candidate.feedback = oneFeedbackOnCandidate;
                candidate.workflow.feedback = candidate.feedback;
            }
        }
        return candidatesWithFeedback;
    };

    const addFeedbackProvidersDataToCandidates = async ({ candidates }) => {
        let candidatesWithFeedbackProviders = [...candidates];
        const feedbackProviderIds = _.reduce(candidates,
            (accumulator, candidate) => {
                const employerRepId = _.get(candidate, 'workflow.feedback.employerRepId', null);
                if (employerRepId && accumulator.indexOf(employerRepId) === -1) {
                    return [...accumulator, employerRepId];
                }

                return accumulator;
            }, []);

        let feedbackProviders = [];

        if (feedbackProviderIds.length) {
            const [{ result: employers }, { result: employerReps }] = await Promise.all([
                EmployerService.getEmployersByIds(feedbackProviderIds),
                EmployerRepService.getEmployersRepsByIds({ employerRepIds: feedbackProviderIds }),
            ]);

            feedbackProviders = _.mapValues(_.keyBy([...employers, ...employerReps], '_id'), 'name');
            candidatesWithFeedbackProviders = candidatesWithFeedbackProviders.map(
                candidate => CandidateService.extendFeedbackData(candidate, feedbackProviders),
            );
        }
        return candidatesWithFeedbackProviders;
    };

    const addPublicApprovalLinksToCandidates = async ({ positionId, candidates, workflows, publicEmailDomains }) => {
        const workflowIdsMap = (workflows || []).reduce((res, workflow) => ({ ...res, [workflow.talentId]: workflow._id }), {});
        const candidatesData = (candidates || []).map((candidate) => {
            const talentId = candidate.talentId || candidate.talentID;
            const candidateData = candidate.candidateData || candidate;
            const emails = _.get(candidateData, 'contact.emailAddress', []);
            const [email] = getSortedEmails({ emails, publicEmailDomains });

            return {
                emails: email ? [email] : [],
                talentId,
                workflowId: workflowIdsMap[talentId],
                channelType: 'email',
            };
        });
        const { result: approvalLinks } = await RouteService.getPositionApprovalLinks(positionId, {
            candidates: candidatesData,
            sentBy: 'recruiter',
        });

        const approvalLinksMap = (approvalLinks || []).reduce((res, linkData) => ({ ...res, [linkData.talentId]: linkData.link }), {});

        return (candidates || []).map((candidate) => ({
            ...candidate,
            publicApprovalUrl: approvalLinksMap[candidate.talentId || candidate.talentID],
        }));
    };

    const getFilteredCandidatesFromWorkflowsAndFeedback = ({ workflows, feedbackCandidates, talentIds, isEmployerMode, isExportAll }) => {
        const workflowsCopy = [...workflows];
        const feedbackCandidatesCopy = [...feedbackCandidates];

        const workflowsFilterFunction = (workflow) => {
            const isNotInList = !isExportAll && !talentIds.includes(workflow.talentId);
            const isHiddenByRecruiter = !isEmployerMode && workflow.recruiterStatus.current === RECRUITER_WORKFLOW_STATUS.HIDDEN;
            const isNotSentToEmployer = isEmployerMode && workflow.recruiterStatus.current !== RECRUITER_WORKFLOW_STATUS.SENT;

            return isNotInList || isHiddenByRecruiter || isNotSentToEmployer;
        };

        const feedbacksFilterFunction = feedbackCandidate =>
            !isExportAll && !talentIds.includes(feedbackCandidate.talentId);

        _.remove(workflowsCopy, workflowsFilterFunction);

        _.remove(feedbackCandidatesCopy, feedbacksFilterFunction);

        const workflowsByTalentId = _.keyBy(workflowsCopy, 'talentId');

        _.remove(feedbackCandidatesCopy, candidate => !!workflowsByTalentId[candidate.talentId]);

        const candidatesFromWorkflows = workflowsCopy.map(workflow => ({
            talentId: workflow.talentId,
            workflow,
            fullName: workflow.candidateName,
        }));

        const candidatesFromFeedback = feedbackCandidatesCopy.map(feedbackCandidate => ({
            talentId: feedbackCandidate.talentId,
            workflow: feedbackCandidate.workflow,
            fullName: feedbackCandidate.name,
        }));

        return [...candidatesFromWorkflows, ...candidatesFromFeedback];
    };

    const getCandidates = async ({ positionId, talentIds = [], isEmployerMode, recruiterId, isExportAll = false }) => {
        const [
            { result: position },
            { result: workflows },
        ] = await Promise.all([
            PositionService.getPosition(positionId),
            WorkflowService.getWorkflowSearch({ positionId }),
        ]);

        const feedbackCandidates = await getFeedbackCandidatesByUserType({
            isEmployerMode,
            positionId,
            employerId: position.employerId,
            recruiterId,
        });

        let candidates = getFilteredCandidatesFromWorkflowsAndFeedback({ workflows, feedbackCandidates, talentIds, isEmployerMode, isExportAll });

        const positionData = position;
        positionData.locationString = CommonCodeService.getLocationString(positionData.location);

        const [
            talentsResponse,
            talentForEmployerResponse,
        ] = await Promise.all([
            TalentService.postTalents({ talentIDs: _.map(workflows, 'talentId') }),
            TalentForEmployerService.searchTalentForEmployer({ positionId }),
        ]);

        if (talentForEmployerResponse.errorCode === 'SUCCESS' && talentsResponse.errorCode === 'SUCCESS') {
            const talents = _.get(talentsResponse, 'result.result', _.get(talentsResponse, 'result', []));
            const talentsForEmployer = _.get(talentForEmployerResponse, 'result.result', _.get(talentsResponse, 'result', []));
            const talentsData = [...talents, ...talentsForEmployer];

            for (const talentData of talentsData) {
                const candidate = _.find(candidates, {
                    talentId: talentData.talentId,
                });
                if (candidate) {
                    candidate.candidateData = talentData;
                    candidate.fullName = candidate.candidateData.name;
                    candidate.candidateData.matchScore = candidate.matchScore;
                    candidate.currentJob = _.find(candidate.candidateData.employmentHistory, { isCurrent: true });
                    candidate.previousJob = _.find(candidate.candidateData.employmentHistory, { isCurrent: false });
                    candidate.predictionScore = candidate.candidateData.predictionScore;
                    candidate.residency = CommonCodeService.getLocationString(candidate.candidateData.residency);
                }
            }

            candidates = await addExplainsDataToCandidates({ candidates, positionData });
            candidates = addFeedbackDataToCandidates({ candidates, positionData });
            candidates = await addFeedbackProvidersDataToCandidates({ candidates, positionData });

            candidates = getCandidatesWithHiddenDetails({ candidates, feedbackCandidates });
            candidates = filterValidCandidatesForExport({ candidates, isEmployerMode, isExportAll });
        }
        return candidates;
    };

    const getSearchCandidates = async ({ positionId, talentIds = [], isEmployerMode, recruiterId, maxItems, filters }) => {
        const [
            { result: position },
            { result: searchCandidates },
        ] = await Promise.all([
            PositionService.getPosition(positionId),
            CandidatesExportService.getCandidatesDataByTalentIds({
                positionId,
                talentIds,
                maxItems,
                filters,
            }),
        ]);

        const feedbackCandidates = await getFeedbackCandidatesByUserType({
            isEmployerMode,
            positionId,
            employerId: position.employerId,
            recruiterId,
        });

        const searchCandidatesWithFeedbackProviders = await addFeedbackProvidersDataToCandidates({
            candidates: searchCandidates,
            positionData: position,
        });

        return getCandidatesWithHiddenDetails({
            candidates: searchCandidatesWithFeedbackProviders,
            feedbackCandidates,
            params: {
                candidateDataParamsToKeep: ['name', 'matchScore', 'contact', 'mainResumeSource'],
            },
        });
    };

    return {
        getCandidates,
        getSearchCandidates,
        getCandidatesCsvData,
        getCvsMetadata,
        getCsvFileName,
        addPublicApprovalLinksToCandidates,
    };
};

angular
    .module('TALENYA-FE')
    .factory('CandidatesListService', CandidatesListService);
